import classnames from "classnames";
import PAText from "component/design-system/typography/PAText/PAText";
import useScreenSize from "hoc/useScreenSize";
import useOutsideClick from "hooks-contexts/useOutsideClick";
import { useTranslation } from "next-i18next";
import {
  useCallback, useEffect, useRef, useState,
} from "react";
import { renderHTML } from "util/DomHelper";
import HideIf from "util/HideIf";
import { debounceWithCallAPI } from "util/lodash/debounceWithCallAPI";
import { isEmpty } from "util/lodash/isEmpty";
import styles from "./searchBar.module.scss";

function SearchBar(props: any) {
  const {
    results = [], onClick, heading, placeholder,
    className, hideResults = false, hideHeading = false,
    hideSearchIcon = false, searchValueHandler, searchClassName = "", fullWidth = false,
    absolutePosition = false,
  } = props;

  const debounceOnSuggestionsFetchRequested = useCallback(
    debounceWithCallAPI(searchValueHandler, 0, null, null),
    [],
  );

  const [isMobile] = useScreenSize();
  const visibleResults = isMobile ? results.slice(0, 6) : results;

  const { t } = useTranslation("common");
  const [value, setValue] = useState();
  const [showSuggestions, setShowSuggestions] = useState(true);
  const suggestionsRef = useRef();
  const noResultsRef = useRef();
  const searchBarRef = useRef();
  // const { t } = useTranslation("common");

  function changeText(event) {
    setShowSuggestions(true);
    const updatedInput = event.target.value || event.target.innerHTML || "";
    setValue(updatedInput);
    debounceOnSuggestionsFetchRequested(updatedInput);
  }

  function keyDownHandler(event) {
    setShowSuggestions(true);
    if (event.keyCode === 40) {
      move(1);
    } else if (event.keyCode === 38) {
      move(-1);
    }
  }
  useEffect(() => {
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  function move(dir: number) {
    if (suggestionsRef.current) {
      const selectables = [...suggestionsRef.current.children];
      const selected = suggestionsRef.current.querySelector(".selected");
      let i = selected ? selectables.indexOf(selected) : 0;
      i = (i + selectables.length + dir) % selectables.length;

      if (selected) {
        selected.classList.remove("selected");
      } else {
        i = 0;
      }
      selectables[i].classList.add("selected");
      setValue(selectables[i].innerText);
      if (searchBarRef && searchBarRef.current) {
        searchBarRef.current.innerHTML = selectables[i].innerText;
      }
    }
  }

  function onClickHandler() {
    if (value && results && results.length > 0) {
      const suggestionData = results.find((eachData) => searchBarRef.current.value === eachData.value);
      onClick(suggestionData || results[0]); // if user hit enter without navigation take the first suggestion
    }
  }

  function searchIconHandler() {
    onClickHandler();
  }

  function onkeydown(event) {
    if (event.keyCode === 13 && !isEmpty(value)) {
      onClickHandler();
    } else if (event.keyCode === 27) {
      searchBarRef.current?.blur();
    }
  }

  function hideSuggestions() {
    if (!absolutePosition) {
      return;
    }

    setShowSuggestions(false);
  }

  useOutsideClick(suggestionsRef, hideSuggestions);
  useOutsideClick(noResultsRef, hideSuggestions);

  return (
    <div
      className={classnames({
        [styles.container]: true,
        [className]: true,
        [styles.full_width]: fullWidth,
      })}
    >
      <HideIf show={!hideHeading}>
        <PAText component="h2">
          {heading}
        </PAText>
      </HideIf>
      <PAText component="div" className={`${styles.search_bar} ${searchClassName}`}>
        <input
          type="text"
          className="m-0 m-r-5"
          ref={searchBarRef}
          value={value}
          placeholder={placeholder}
          onKeyDown={onkeydown}
          onInput={changeText}
          onFocus={() => setShowSuggestions(true)}
        />

        <HideIf show={!hideSearchIcon}>
          <i className="fa fa-search" onClick={searchIconHandler} />
        </HideIf>
      </PAText>

      <HideIf show={!hideResults && value && results && results.length > 0 && showSuggestions}>

        <div ref={suggestionsRef} className={`${styles.results_container} ${absolutePosition ? styles.results_container_absolute : ""}`}>
          {visibleResults.map((eachSuggestionInner, index: number) => (

            <span
              key={`${eachSuggestionInner.highlighted}-${index}`}
              className={styles.each_list}
              onClick={() => onClick(eachSuggestionInner)}
            >
              <i className="fa fa-search" />
              <HideIf show={eachSuggestionInner.highlighted}>
                <span className="m-r-5" dangerouslySetInnerHTML={renderHTML(eachSuggestionInner.highlighted)} />
              </HideIf>
              <HideIf show={!eachSuggestionInner.highlighted && eachSuggestionInner.value}>
                <span className="m-r-5">
                  {eachSuggestionInner.value}
                </span>
              </HideIf>

            </span>

          ))}
        </div>
      </HideIf>
      <HideIf show={!hideResults && value && (!results || results.length === 0) && showSuggestions}>
        <div className={`${styles.no_results_container} ${absolutePosition ? styles.results_container_absolute : ""}`} ref={noResultsRef}>
          {t("0 Results Found")}
          .
        </div>
      </HideIf>
    </div>
  );
}

export default SearchBar;
